import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Breadcrumb,
  Card,
  Form,
  Row,
  Col,
  FormGroup,
  Button,
  Container
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages
} from "@whitespace/whitespace-ui";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IAddStaffOnSiteRequest from "interfaces/Request/IAddStaffOnSiteRequest";
import addStaffOnSiteSchema from "modules/schemas/addStaffOnSiteSchema";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { convertToDateFromLocalDate } from "helpers/DateUtils";
import "styles/form.scss";

const AddStaffOnSite = () => {
  const navigate = useNavigate();
  const { hwrcSiteId, diaryDate } = useParams();
  const returnUrl = `/diary${
    hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""
  }`;
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddStaffOnSiteRequest>({
    resolver: yupResolver(addStaffOnSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [timeInInput] = useState<Date | undefined>(new Date());
  const [isStartTimeAfterEndTimeError, setIsStartTimeAfterEndTimeError] = useState<boolean>(false);
  const [timeInRef, setTimeInRef] = useState<TimePickerComponent | undefined>();
  const [timeOutRef, setTimeOutRef] = useState<
  TimePickerComponent | undefined
  >();

  const {
    isSending,
    errors: apiErrors,
    data,
    sendRequest,
    statusCode
  } = useAmplifyREST();

  const handleNavToDiary = () => {
    navigate(returnUrl);
  };

  const isTimeComponentSet = (
    timeComponent: TimePickerComponent | undefined
  ): boolean => !!(timeComponent && timeComponent.value);

  const canFormBeSubmitted = ():boolean => {
    if (
      timeInRef?.value
      && timeOutRef?.value
      && timeOutRef.value.getTime() <= timeInRef!.value.getTime()
    ) {
      setIsStartTimeAfterEndTimeError(true);
      return false;
    }

    setIsStartTimeAfterEndTimeError(false);
    const canBeSubmitted = (getValues("name").length > 0 && isTimeComponentSet(timeInRef) && timeInRef?.value !== undefined);
    setAddButtonDisabled(
      !canBeSubmitted
    );

    return canBeSubmitted;
  };

  const handleStaffOnSiteSubmitHandler = (request: IAddStaffOnSiteRequest) => {
    const timeIn = convertToDateFromLocalDate(diaryDate!);
    timeIn.setHours(timeInRef!.value.getHours());
    timeIn.setMinutes(timeInRef!.value.getMinutes());
    request.timeIn = timeIn.toISOString();
    if (isTimeComponentSet(timeOutRef) && timeOutRef?.value !== undefined) {
      const timeOut = convertToDateFromLocalDate(diaryDate!);
      timeOut.setHours(timeOutRef!.value.getHours());
      timeOut.setMinutes(timeOutRef!.value.getMinutes());
      request.timeOut = timeOut.toISOString();
    }
    sendRequest(
      `hwrcsitediary/hwrcsite/${hwrcSiteId}/diary/${diaryDate}/staffonsite`,
      "POST",
      request
    );
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    if (data && statusCode === 200) handleNavToDiary();
  }, [data, statusCode]);

  useEffect(() => {
    if (statusCode === 401) navigate(RoutePathsEnum.Unauthorised);
  }, [statusCode]);

  useEffect(() => {
    if (apiErrors) setShowExceptions(true);
  }, [apiErrors]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />

      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="add-staff-on-site">
              Create Staff on Site Entry
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleStaffOnSiteSubmitHandler)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    active
                    data-testid="breadcrumb-add-staff-on-site"
                  >
                    Diary / Add Staff
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                  aria-label="create staff on site"
                >
                  Save
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={handleNavToDiary}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to site diary"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="staffOnSite pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mb-3 mt-3">
                      <FormGroup as={Row} controlId="staffName">
                        <Form.Label
                          id="staffNameLabel"
                          className="horiz-form-label"
                        >
                          Staff Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                    <Row xs={12} md={12} className="mb-3 mt-3">
                      <Col>
                        <Form.Group as={Row} className="mb-3">
                          <Form.Label
                            id="timeInLabel"
                            data-testid="timeInLabel"
                            htmlFor="timeInInput"
                            column
                            sm="2"
                          >
                            Time In:
                          </Form.Label>
                          <Col sm="4">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => {
                                setTimeInRef(t);
                              }}
                              id="timeInInput"
                              data-testid="timeIn"
                              type="text"
                              htmlAttributes={{
                                "aria-labelledby": "timeInLabel"
                              }}
                              format="HH:mm"
                              value={timeInInput}
                              onChange={() => {
                                canFormBeSubmitted();
                              }}
                              showClearButton={false}
                            />
                          </Col>
                          {isStartTimeAfterEndTimeError && (
                            <span
                              data-testid="timeInAfterTimeOut"
                              className="validation-error"
                            >
                              Must be after the time In
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group as={Row}>
                          <Form.Label
                            id="timeOutLabel"
                            data-testid="timeOutLabel"
                            htmlFor="timeOutInput"
                            column
                            sm="2"
                          >
                            Time Out:
                          </Form.Label>
                          <Col sm="4">
                            <TimePickerComponent
                              style={{ marginLeft: "2%" }}
                              ref={(t: TimePickerComponent) => {
                                setTimeOutRef(t);
                              }}
                              id="timeOutInput"
                              data-testid="timeOut"
                              type="text"
                              htmlAttributes={{
                                "aria-labelledby": "timeOutLabel"
                              }}
                              format="HH:mm"
                              onChange={() => {
                                canFormBeSubmitted();
                              }}
                              showClearButton={false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="cotcQualified"
                      >
                        <Form.Label
                          id="cotcQualifiedLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          COTC Qualified:
                        </Form.Label>

                        <Form.Check
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="cotcQualifiedLabel"
                          defaultChecked
                          aria-required="true"
                          onKeyUp={() => canFormBeSubmitted()}
                          {...register("cotcQualified")}
                        />

                        <ErrorMessages
                          fieldName="cotcQualified"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="notes">
                        <Form.Label
                          id="notesLabel"
                          className="horiz-form-label"
                        >
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck(
                              "notes",
                              errors,
                              null
                            )}
                            aria-labelledby="notesLabel"
                            maxLength={100}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("notes")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="notes"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                  </div>
                </Row>
              </Container>
            </section>
          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AddStaffOnSite;
